import { readItemField } from './item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from './inventory-raw-readers';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

export interface InventoryDefinitionCatalogMetadata {
    /** Raw registry presence and NamedIcon labels, not compiled catalog entries. */
    staticIds: readonly string[];
    referenceNames: Readonly<Record<string, string>>;
    /** Complete raw items-table identities, including explicit unavailable refs. */
    identityRows?: Readonly<Record<string, Row | null>>;
}
const stripTypes = new Set(['roll_type', 'range_type', 'damage_type', 'sphere_of_magic', 'size_type',
    'defensive_skill', 'innate_skill', 'armor_skill', 'circle_of_magic', 'class', 'subclass', 'creature',
    'passive_ability', 'spell', 'terrain_type', 'status_effect', 'item_composition', 'weapon_classification',
    'natural_weapon', 'attack_ability', 'aoe_type', 'equipment_slot', 'equipment_visual_mount', 'vitality_status',
    'essence', 'armor_type', 'creature_type', 'creature_sub_type', 'creature_sub_type_ancestral',
    'creature_sub_type_grouping', 'creature_ancestry', 'creature_grouping', 'creature_lineage', 'weapon_skill',
    'knowledge_skill', 'speed_type', 'ancestry', 'location_type', 'light_type', 'environment', 'weather_type',
    'weather', 'temperature', 'tempurature_type', 'wind_type', 'team_type', 'full_ability', 'ability',
    'quick_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'feat', 'charged_ability',
    'curse', 'sanguine', 'menta', 'lineage', 'background', 'lifestyle']);
const forceTypes = new Set(['armor_type', 'creature_type', 'creature_sub_type', 'creature_sub_type_ancestral',
    'creature_sub_type_grouping', 'creature_ancestry', 'creature_grouping', 'creature_lineage', 'weapon_skill',
    'knowledge_skill', 'status_effect', 'ancestry']);
const deniedTypes = new Set(['creature', 'player_character', 'encounter', 'encounter_creature', 'campaign',
    'location', 'adventure', 'source']);
const array = (value: unknown): value is Row | unknown[] => value !== null && typeof value === 'object';
const nonempty = (value: unknown): boolean => array(value) && Object.keys(value).length > 0;

export function elideEmptyInventoryDefinitionLayers(source: Row): Row {
    const result = fromBaseClone(source);
    function walk(node: Row | unknown[]): void {
        if (fromBaseRecord((node as Row).items) && array((node as Row).item_layer_types)
            && !Object.keys((node as Row).item_layer_types as Row).length) delete (node as Row).item_layer_types;
        for (const child of Object.values(node)) if (array(child)) walk(child);
    }
    walk(result); return result;
}

/** query_full_item_arr's definition-only catalog pass. Instance thinning is
 * deliberately absent: query_inventory_arr retains its full instance surface. */
export function internInventoryDefinition(source: Row, metadata: InventoryDefinitionCatalogMetadata,
    definition: (id: string) => Row | null): Row {
    const result = fromBaseClone(source), catalog: Record<string, Row> = {}, riding: Record<string, Row> = {};
    const staticIds = new Set(metadata.staticIds);
    const identity = (row: Row): string | null => {
        const id = readItemField(row, 'item_id');
        return fromBaseUuid(id) ? id : null;
    };
    function lift(node: Row | unknown[]): void {
        if (fromBaseRecord((node as Row).catalog)) {
            for (const [id, entry] of Object.entries((node as Row).catalog as Row))
                if (fromBaseRecord(entry) && !(id in riding)) riding[id] = entry;
            delete (node as Row).catalog;
        }
        for (const child of Object.values(node)) if (array(child)) lift(child);
    }
    function internIdentity(row: Row, id: string, force = false): void {
        const type = String(readItemField(row, 'item_type') ?? '');
        const description = type === 'status_effect' ? readItemField(row, 'item_description') : null;
        if (!(id in catalog) && (force || forceTypes.has(type) || !staticIds.has(id))) {
            catalog[id] = { item_name: readItemField(row, 'item_name') ?? '', item_type: type };
        }
        if (description && catalog[id] && 'item_name' in catalog[id] && !catalog[id].item_description)
            catalog[id].item_description = description;
    }
    function shell(row: Row): boolean {
        const computed = fromBaseRecord(row.computed) ? row.computed : {};
        return fromBaseRecord(row.items) && !nonempty(row.item_layer_types)
            && !Object.keys(computed).some(key => key !== 'pl')
            && !Object.keys(row).some(key => !['items', 'computed', 'item_layer_types'].includes(key))
            && stripTypes.has(String(readItemField(row, 'item_type')));
    }
    function full(row: Row): boolean {
        return fromBaseRecord(row.items) && nonempty(row.item_layer_types)
            && !deniedTypes.has(String(readItemField(row, 'item_type')))
            && !readInventoryField(row, 'inventory_id')
            && !['encounter_creatures', 'player_characters', 'statuses', 'position', 'pos_arr'].some(key => array(row[key]));
    }
    function walk(node: Row | unknown[], pass: 'strip' | 'full'): void {
        for (const [key, value] of Object.entries(node)) {
            if (!array(value)) continue;
            const child = value as Row, id = identity(child);
            if (key !== 'status_arr' && id && (pass === 'strip' ? shell(child) : full(child))) {
                if (pass === 'strip') internIdentity(child, id);
                else store(id, child);
                (node as Row)[key] = id;
            } else walk(value, pass);
        }
    }
    function store(id: string, source: Row): void {
        if (nonempty(catalog[id]?.item_layer_types)) return;
        catalog[id] = { item_layer_types: { pending: true } };
        const entry = fromBaseClone(source);
        delete entry.computed; delete entry.item_name; delete entry.item_type;
        walk(entry, 'full');
        catalog[id] = Object.fromEntries([...new Set(['items', 'pictures', 'assets', 'item_videos',
            ...Object.keys(entry).filter(name => name !== 'item_layer_types'), 'item_layer_types'])]
            .filter(name => Object.prototype.hasOwnProperty.call(entry, name)).map(name => [name, entry[name]]));
    }
    function shells(node: Row | unknown[]): void {
        const row = node as Row, id = identity(row);
        if (id && fromBaseRecord(row.items) && !nonempty(row.item_layer_types) && !nonempty(row.computed)) {
            internIdentity(row, id); return;
        }
        for (const child of Object.values(node)) if (array(child)) shells(child);
    }
    function referenced(node: Row | unknown[]): void {
        for (const value of Object.values(node)) {
            for (const entry of array(value) ? Object.values(value) : [value]) {
                if (typeof entry !== 'string' || !fromBaseUuid(entry) || entry in catalog) continue;
                const name = metadata.referenceNames[entry];
                if (name && name !== 'Na') catalog[entry] = { item_name: name };
            }
            if (array(value)) referenced(value);
        }
    }
    function charged(node: Row | unknown[], names: readonly string[]): void {
        for (const [key, value] of Object.entries(node)) {
            if (names.includes(key) && array(value)) {
                const cells = Object.keys(value).every((key, index) => key === String(index)) ? Object.values(value) : [value];
                for (const cell of cells) {
                    const entry = readLayerListEntryUuid(cell);
                    if (!fromBaseUuid(entry) || nonempty(catalog[entry]?.item_layer_types)) continue;
                    const child = definition(entry);
                    if (child) {
                        // These explicit reference rescues drop the queried
                        // definition's catalog before storing its own surface.
                        const copy = fromBaseClone(child); delete copy.catalog;
                        store(entry, copy);
                    }
                }
            }
            if (array(value)) charged(value, names);
        }
    }
    function rescuedIdentities(node: Row | unknown[]): void {
        for (const [key, value] of Object.entries(node)) {
            if (key === 'summon' || key === 'shapeshift' || /^user_choice_\d+_(shapeshift|summon)$/.test(key)) {
                for (const entry of array(value) ? Object.values(value) : [value]) {
                    if (!fromBaseUuid(entry) || entry in catalog) continue;
                    if (!metadata.identityRows || !Object.prototype.hasOwnProperty.call(metadata.identityRows, entry))
                        throw new FromBaseStageMissing('definition-catalog-identity-input', entry);
                    const identity = metadata.identityRows[entry], name = readItemField(identity ?? null, 'item_name');
                    if (typeof name === 'string' && name !== '' && name !== 'Na') catalog[entry] = {
                        item_name: name, item_type: readItemField(identity ?? null, 'item_type') || 'creature' };
                }
            }
            if (array(value)) rescuedIdentities(value);
        }
    }
    lift(result); walk(result, 'strip'); walk(result, 'full');
    for (const [id, entry] of Object.entries(riding)) {
        if (!(id in catalog) || !nonempty(catalog[id]?.item_layer_types) && nonempty(entry.item_layer_types)) catalog[id] = entry;
    }
    shells(result);
    for (const names of [['charged_spell', 'charged_ability'], ['passive_ability']]) {
        charged(result, names);
        for (const entry of Object.values(catalog)) charged(entry, names);
    }
    rescuedIdentities(result);
    for (const entry of Object.values(catalog)) rescuedIdentities(entry);
    referenced(result);
    for (const entry of Object.values(catalog)) referenced(entry);
    if (Object.keys(catalog).length) result.catalog = Object.fromEntries(Object.keys(catalog).sort().map(id => [id, catalog[id]]));
    return elideEmptyInventoryDefinitionLayers(result);
}
